import { z } from 'zod';
import { HstTreatmentSchema, ShiftSettingSchema, ShiftTimeBucketSchema } from '../rate/enums.schema.js';
import { OfferStatusSchema, ShiftStatusSchema } from './enums.schema.js';
import { ShiftListQuerySchema } from './list-scope.schema.js';
// Phase 9d — caregiver portal response shapes.
//
// The matching engine + lifecycle service already speak ShiftOffer +
// Shift + Recipient. The caregiver UI needs richer context: location
// label, distance, role label, and an "if you accept now" rate preview
// for offers. These views compose existing shapes + the joined data
// the controller resolves at read time.
const RatePreviewSchema = z.object({
    payRate: z.string(), // resolved snapshot at view time
    // MARGIN BOUNDARY (audit C5): billRate (what TrueCare charges the buyer) is
    // NEVER sent to a caregiver — the pre-acceptance offer preview leaked it, so
    // any caregiver could compute the platform's exact margin from the Network
    // tab. Mirrors the same removal already made on the booked-shift shape below.
    // Do NOT re-add billRate here.
    hstTreatment: HstTreatmentSchema,
    shiftTimeBucket: ShiftTimeBucketSchema,
    isStatHoliday: z.boolean(),
    modifierCodes: z.array(z.string()),
});
const ShiftRequestContextSchema = z.object({
    shiftRequestId: z.string().uuid(),
    roleCode: z.string(),
    setting: ShiftSettingSchema,
    scheduledStart: z.string().datetime(),
    scheduledEnd: z.string().datetime(),
    /**
     * Human-readable label: facility name for LTC shifts;
     * "Recipient name (City)" for HOMECARE shifts. No PHI.
     */
    locationLabel: z.string(),
    /**
     * Facility street address for LTC offers (shown when the caregiver taps the
     * facility name). Null for HOMECARE — the client's home address isn't shared
     * before booking.
     */
    locationAddress: z.string().nullable(),
    distanceKm: z.number().nullable(),
});
export const CaregiverOfferListItemSchema = z.object({
    id: z.string().uuid(),
    tier: z.number().int(),
    targetedByOperator: z.boolean(),
    targetReason: z.string().nullable(),
    offeredAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    status: OfferStatusSchema,
    context: ShiftRequestContextSchema,
    ratePreview: RatePreviewSchema,
});
export const CaregiverOfferListResponseSchema = z.object({
    items: z.array(CaregiverOfferListItemSchema),
});
export const CaregiverOfferListQuerySchema = z.object({
    includeExpired: z.coerce.boolean().default(false),
});
export const CaregiverShiftListItemSchema = z.object({
    id: z.string().uuid(),
    shiftRequestId: z.string().uuid(),
    status: ShiftStatusSchema,
    setting: ShiftSettingSchema,
    scheduledStart: z.string().datetime(),
    scheduledEnd: z.string().datetime(),
    // MARGIN BOUNDARY — a care professional sees ONLY their own pay.
    //
    // `billRateAtBooking` (what TrueCare charges the buyer) was being shipped
    // here too, so any caregiver could compute the platform's exact spread
    // (bill − pay). It was never even rendered — a pure leak. The spread is the
    // platform's margin (Q2 managed-marketplace model); it is not caregiver-
    // visible. Do NOT re-add billRateAtBooking to this shape.
    //
    // (The operator's own AdminShiftListItem shape keeps BOTH rates — that one
    //  is correct: the margin is Terry's to see.)
    payRateAtBooking: z.string(),
    hstTreatmentAtBooking: HstTreatmentSchema,
    shiftTimeBucketAtBooking: ShiftTimeBucketSchema,
    acceptedViaManualAssign: z.boolean(),
    actualStart: z.string().datetime().nullable(),
    actualEnd: z.string().datetime().nullable(),
    hoursActual: z.string().nullable(),
    cancelledAt: z.string().datetime().nullable(),
    cancelActorPersona: z.string().nullable(),
    cancelReason: z.string().nullable(),
    roleCode: z.string(),
    locationLabel: z.string(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CaregiverShiftListResponseSchema = z.object({
    items: z.array(CaregiverShiftListItemSchema),
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
/** Doc 17 — the shared shift-list query (scope + optional status + paging). */
export const CaregiverShiftListQuerySchema = ShiftListQuerySchema;
/** Shared `{ reason }` body for cancel + dispute caregiver actions. */
export const CaregiverShiftActionWithReasonSchema = z.object({
    reason: z.string().min(10).max(500),
});
//# sourceMappingURL=caregiver-views.schema.js.map